import React, {useEffect, useState} from "react";
import QSelect from "../../../../common/components/QSelect";
import SetupApi from "../../../../common/api/functions/Setup";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {motion, AnimatePresence} from "framer-motion";
import {
    AccountInterface,
    JournalInterface,
} from "../../../../interfaces/economic/CommonInterface";
import {SelectOptionInterface} from "../../../../interfaces/SelectOptionInterface";
import {useDispatch} from "react-redux";
import {setLoading} from "../../../../app/rootReducer";
import {FormGroup, Input} from "reactstrap";
import {useTranslation} from "react-i18next";
import SetupOptionsError from "./SetupOptionsError";
import {useSeedCommonDefaults} from "../helpers/useSeedCommonDefaults";

interface Props {
    formik: any
}

const Payment: React.FC<Props> = ({formik}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [cashbooks, setCashbooks] = useState<JournalInterface[]>([]);
    const [accounts, setAccounts] = useState<AccountInterface[]>([]);
    const [loadError, setLoadError] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useSeedCommonDefaults(formik);

    useEffect(() => {
        let cancelled = false;
        setOptionsLoading(true);
        setLoadError(false);
        SetupApi.getDataSetupPayment().then(async (response: ResponseInterface | any) => {
            if (cancelled) return;
            let {cashbooks, accounts} = response?.data?.data || {};
            setCashbooks(cashbooks || []);
            setAccounts(accounts || []);
            dispatch(setLoading(false));
        }).catch(() => {
            if (!cancelled) {
                setLoadError(true);
                dispatch(setLoading(false));
            }
        }).finally(() => {
            if (!cancelled) setOptionsLoading(false);
        });
        return () => { cancelled = true; };
    }, [dispatch, reloadKey]);

    return (
        <React.Fragment>
            {loadError ? (
                <SetupOptionsError
                    loading={optionsLoading}
                    onRetry={() => setReloadKey((k) => k + 1)}
                />
            ) : null}
            <QSelect
                label={t("economic.cashbook")}
                value={formik.values.economic_payment_cashbook_number}
                onChange={(option: SelectOptionInterface) => {
                    formik.setFieldValue('economic_payment_cashbook_number', option.value);
                }}
                options={cashbooks.map((a: JournalInterface) => ({label: a.name, value: a.journalNumber}))}
                error={formik.errors?.economic_payment_cashbook_number}
            />
            <QSelect
                label={t("economic.bankAccount")}
                value={formik.values.economic_payment_bank_account_number}
                onChange={(option: SelectOptionInterface) => {
                    formik.setFieldValue('economic_payment_bank_account_number', option.value);
                }}
                options={accounts.map((a: AccountInterface) => ({
                    label: a.accountNumber + ' - ' + a.name,
                    value: a.accountNumber
                }))}
                error={formik.errors?.economic_payment_bank_account_number}
            />
            <QSelect
                label={t("economic.feeAccount")}
                value={formik.values.economic_payment_fee_account_number}
                onChange={(option: SelectOptionInterface) => {
                    formik.setFieldValue('economic_payment_fee_account_number', option.value);
                }}
                options={accounts.map((a: AccountInterface) => ({
                    label: a.accountNumber + ' - ' + a.name,
                    value: a.accountNumber
                }))}
                error={formik.errors?.economic_payment_fee_account_number}
            />
            <QSelect
                label={t("economic.differenceAccountCurrency")}
                value={formik.values.economic_payment_difference_currency_account_number}
                onChange={(option: SelectOptionInterface) => {
                    formik.setFieldValue('economic_payment_difference_currency_account_number', option.value);
                }}
                options={accounts.map((a: AccountInterface) => ({
                    label: a.accountNumber + ' - ' + a.name,
                    value: a.accountNumber
                }))}
                error={formik.errors?.economic_payment_difference_currency_account_number}
            />

            <FormGroup switch>
                <label>{t("economic.matchingInvoice")}?</label>
                <Input
                    type="switch"
                    checked={!!formik.values.economic_payment_matching_invoice}
                    onChange={(e) => {
                        formik.setFieldValue('economic_payment_matching_invoice', e.target.checked, false);
                    }}
                />
            </FormGroup>
            <AnimatePresence>
                {!formik.values.economic_payment_matching_invoice && (
                    <motion.div
                        initial={{opacity: 0, height: 0}}
                        animate={{opacity: 1, height: "auto"}}
                        exit={{opacity: 0, height: 0}}
                        transition={{duration: 0.3, ease: "easeInOut"}}
                    >
                        <QSelect
                            label={t("economic.creditAccount")}
                            value={formik.values.economic_payment_credit_account_number}
                            onChange={(option: SelectOptionInterface) => {
                                formik.setFieldValue('economic_payment_credit_account_number', option.value);
                            }}
                            options={accounts.map((a: AccountInterface) => ({
                                label: a.accountNumber + ' - ' + a.name,
                                value: a.accountNumber
                            }))}
                            error={formik.errors?.economic_payment_credit_account_number}
                        /></motion.div>)}
            </AnimatePresence>
        </React.Fragment>
    )
}

export default Payment;
